import React, { useEffect } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';

import { TypingContentRow, useRealTimeTyping } from '@/ai';

import LearningObjectivesList from './learningObjectivesList';

import getAddGenerativeLearningObjectivesModal from '../helpers/getAddGenerativeLearningObjectivesModal';

import styles from '../styles/learningObjectives.module.scss';

const AiGenerateLearningObjectives = ({
  addGeneratedLearningObjective = () => {},
  channelName = '',
  generationStatus = 'idle',
  generateLearningObjectives = () => {},
  handleActionItemClick = () => {},
  handleKebabMenuItemClick = () => {},
  isLearningObjectivesDisplayed = false,
  isReadOnly = false,
  isSourceMaterialProcessed = false,
  orderedLearningObjectives = {},
  renderCustomPlaceholder = () => {},
  renderListItem = () => {},
  reorderLearningObjectives = () => {},
  shouldShowEmptyState = false,
  shouldShowNestedRows = false,
  shouldShowLoadingState = true,
}) => {
  const { connect, disconnect, glow, GeneratingToast, isConnected, onTypingEnd, typingContent } =
    useRealTimeTyping({
      channelName,
      handlers: { complete: () => disconnect() },
      options: {
        defaultGenerateToastText: i18n.t('learning-objectives.generating-content'),
        shouldAutoConnect: false,
      },
    });
  const isInProgress = generationStatus === 'requested' || generationStatus === 'created';

  useEffect(() => {
    if (isInProgress) connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInProgress]);

  useEffect(() => {
    if (isConnected && !isInProgress) {
      generateLearningObjectives();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, isInProgress]);

  const handleConnect = () => {
    connect();
  };

  const handleOnAnimationComplete = (message) => {
    addGeneratedLearningObjective(message);
    onTypingEnd();
  };

  const handleAiGenerateClick = async () => {
    const hasSeenDisclaimer =
      localStorage.getItem('hideLearningObjectivesGenerationDisclaimer') === 'true';

    if (!hasSeenDisclaimer) {
      const { type } = await getAddGenerativeLearningObjectivesModal();

      if (type === 'generate') {
        handleConnect();
        localStorage.setItem('hideLearningObjectivesGenerationDisclaimer', 'true');
      }
    } else {
      handleConnect();
    }
  };

  const renderTypingEffect = () => {
    if (isConnected) {
      return (
        <>
          <TypingContentRow
            typingContent={typingContent}
            onAnimationComplete={handleOnAnimationComplete}
            key={typingContent?.id}
          />
          <GeneratingToast
            className={styles.generatingState}
            testId="learning-objectives-generating-toast"
          />
        </>
      );
    }

    return null;
  };

  return (
    <LearningObjectivesList
      glow={glow}
      handleActionItemClick={handleActionItemClick}
      handleAiGenerateClick={handleAiGenerateClick}
      handleKebabMenuItemClick={handleKebabMenuItemClick}
      isAiGenerateButtonDisabled={!isSourceMaterialProcessed}
      isGenerating={isConnected}
      isReadOnly={isReadOnly}
      isLearningObjectivesDisplayed={isLearningObjectivesDisplayed}
      renderCustomPlaceholder={renderCustomPlaceholder}
      renderListItem={renderListItem}
      renderTypingEffect={renderTypingEffect}
      orderedLearningObjectives={orderedLearningObjectives}
      reorderLearningObjectives={reorderLearningObjectives}
      shouldShowAiGenerateButton
      shouldShowEmptyState={shouldShowEmptyState && !isConnected}
      shouldShowNestedRows={shouldShowNestedRows}
      shouldShowLoadingState={shouldShowLoadingState}
    />
  );
};

AiGenerateLearningObjectives.propTypes = {
  addGeneratedLearningObjective: PropTypes.func,
  channelName: PropTypes.string,
  generationStatus: PropTypes.oneOf(['failure', 'idle', 'in-progress', 'success']),
  generateLearningObjectives: PropTypes.func,
  handleActionItemClick: PropTypes.func,
  handleKebabMenuItemClick: PropTypes.func,
  isLearningObjectivesDisplayed: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isSourceMaterialProcessed: PropTypes.bool,
  orderedLearningObjectives: PropTypes.shape({}),
  renderCustomPlaceholder: PropTypes.func,
  renderListItem: PropTypes.func,
  reorderLearningObjectives: PropTypes.func,
  shouldShowEmptyState: PropTypes.bool,
  shouldShowNestedRows: PropTypes.bool,
  shouldShowLoadingState: PropTypes.bool,
};

export default AiGenerateLearningObjectives;
